import { useEffect, useState, useCallback } from "react";
import api, { API, tokenStore } from "@/lib/api";
import { startPoll } from "@/lib/poll";
import { timeAgo, formatApiError, limitWords, countWords } from "@/lib/format";
import { toast } from "sonner";

// opens in a new tab so videos stream and play instead of downloading first
export async function openFile(uid, f, base = "undertakings") {
  const win = window.open("", "_blank");
  try {
    await api.get("/auth/me"); // refreshes the access token if it expired
    const url = `${API}/${base}/${uid}/files/${f.id}?auth=${encodeURIComponent(tokenStore.access)}`;
    if (win) win.location.href = url;
    else window.location.href = url;
  } catch (err) {
    win?.close();
    toast.error("could not open file");
  }
}

function FailModal({ onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in" style={{ background: "rgba(0,0,0,0.75)" }} onClick={onCancel} data-testid="fail-reason-overlay">
      <div className="w-full max-w-md p-8" style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="fail-reason-modal">
        <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>reason (optional, shown to the undertaker)</span>
        <textarea data-testid="fail-reason-input" value={reason} onChange={(e) => setReason(limitWords(e.target.value, reason, 33))} rows={3} autoFocus className="w-full p-3 mt-3 text-sm resize-none" style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
        <p className="text-xs mt-1 text-right" style={{ color: "var(--text-muted)" }}>{countWords(reason)}/33</p>
        <div className="flex gap-3 mt-6">
          <button onClick={onCancel} data-testid="fail-reason-cancel" className="btn flex-1 py-3 text-sm tracking-widest">CANCEL</button>
          <button onClick={() => onConfirm(reason)} disabled={busy} data-testid="fail-reason-confirm" className="btn flex-1 py-3 text-sm tracking-widest">{busy ? "…" : "FAIL"}</button>
        </div>
      </div>
    </div>
  );
}

function ReviewItem({ u, onDone }) {
  const [busy, setBusy] = useState(false);
  const [failing, setFailing] = useState(false);
  const act = async (kind, reason) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/undertakings/${u.id}/${kind}`, kind === "fail" ? { reason } : undefined);
      toast.success(kind === "complete" ? `confirmed · ${u.provisions} provisions sent` : "failed · back to the pile");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <li data-testid={`review-item-${u.id}`} className="py-6 px-4 fade-in-up" style={{ borderBottom: "1px solid var(--border)" }}>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="text-xs tracking-widest ml-auto" style={{ color: "var(--accent)" }}>{u.provisions} provisions</span>
      </div>
      <p className="long-text font-head font-light text-lg leading-relaxed" style={{ color: "var(--text-primary)" }}>{u.text}</p>
      <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>
        delegator: {u.author?.name || "unknown"} · undertaker: {u.undertaker?.name || "unknown"} · sent up {timeAgo(u.sent_up_at)}
      </p>
      <div className="flex flex-wrap gap-2 mt-4">
        {(u.files || []).map((f) => (
          <button key={f.id} onClick={() => openFile(u.id, f)} data-testid={`review-file-${f.id}`} className="btn px-3 py-2 text-[10px] tracking-widest">{f.name}</button>
        ))}
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={() => setFailing(true)} disabled={busy} data-testid={`review-fail-${u.id}`} className="btn flex-1 py-3 text-sm tracking-widest">FAIL</button>
        <button onClick={() => act("complete")} disabled={busy} data-testid={`review-complete-${u.id}`} className="btn flex-1 py-3 text-sm tracking-widest">CONFIRM COMPLETION</button>
      </div>
      {failing && <FailModal busy={busy} onCancel={() => setFailing(false)} onConfirm={(r) => act("fail", r)} />}
    </li>
  );
}

export function ReviewTab() {
  const [items, setItems] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => api.get("/undertakings/review").then(({ data }) => setItems(data)).catch((e) => console.debug("review load", e)), []);
  useEffect(() => { load(); return startPoll(load, 8000); }, [load]);
  return (
    <div>
      {!items ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
      ) : !items.length ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="review-empty">Nothing submitted for review.</p>
      ) : (
        <ul data-testid="review-list">{items.map((u) => <ReviewItem key={u.id} u={u} onDone={() => { load(); setTick((t) => t + 1); }} />)}</ul>
      )}
      <CompletedReview key={tick} />
    </div>
  );
}

function BraveFlagModal({ u, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/undertakings/${u.id}/flag`, { category: "brave", appreciation: text });
      toast.success("sent to only the brave");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in" style={{ background: "rgba(0,0,0,0.75)" }} onClick={onClose} data-testid="brave-flag-overlay">
      <div className="w-full max-w-md p-8 max-h-[85vh] flex flex-col" style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="brave-flag-modal">
        <div className="flex justify-between items-start mb-6 gap-4 shrink-0">
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>send to only the brave</span>
          <button onClick={onClose} data-testid="brave-flag-close" className="text-sm opacity-60 hover:opacity-100" style={{ color: "var(--text-primary)" }}>[ close ]</button>
        </div>
        <p className="long-text-sm text-sm mb-6 overflow-y-auto" style={{ color: "var(--text-primary)" }}>{u.text}</p>
        <label className="text-xs uppercase tracking-widest shrink-0" style={{ color: "var(--text-muted)" }}>appreciation</label>
        <textarea data-testid="brave-flag-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="why is this brave…" className="w-full p-3 mt-2 text-sm resize-none shrink-0" style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
        <button onClick={submit} disabled={busy} data-testid="brave-flag-submit" className="btn w-full py-3 mt-6 text-sm tracking-widest shrink-0">{busy ? "…" : "FLAG"}</button>
      </div>
    </div>
  );
}

export function CompletedReview() {
  const [items, setItems] = useState(null);
  const [flagging, setFlagging] = useState(null);
  const load = useCallback(() => api.get("/undertakings/completed").then(({ data }) => setItems(data)).catch((e) => console.debug("completed load", e)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-24" data-testid="completed-review">
      <p className="text-xs uppercase tracking-widest mb-4" style={{ color: "var(--text-muted)" }}>completed undertakings</p>
      {!items ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
      ) : !items.length ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="completed-review-empty">none yet.</p>
      ) : (
        <ul>
          {items.map((u) => (
            <li key={u.id} data-testid={`completed-review-item-${u.id}`} className="py-5 px-4" style={{ borderBottom: "1px solid var(--border)" }}>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-xs" style={{ color: "var(--accent)" }}>{u.provisions} provisions</span>
                <button onClick={() => setFlagging(u)} disabled={u.flagged} data-testid={`completed-review-flag-${u.id}`} className="btn ml-auto px-3 py-1 text-[10px] tracking-widest">
                  {u.flagged ? "FLAGGED" : "FLAG"}
                </button>
              </div>
              <p className="long-text-sm text-sm" style={{ color: "var(--text-secondary)" }}>{u.text}</p>
              <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
                undertaker: {u.undertaker_name || "unknown"} · delegator: {u.author?.name || "unknown"} · {timeAgo(u.completed_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
      {flagging && <BraveFlagModal u={flagging} onClose={() => setFlagging(null)} onDone={() => { setFlagging(null); load(); }} />}
    </div>
  );
}
